import {
  ActionCard,
  Button,
  ConfirmationDialog,
  Stack,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'component-library';
import { lazy, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Url } from '@/components/Url';
import { MediaFilePreview } from '@/components/widgets/o/Map/MediaMap/MediaFilePreview';
import { BUTTON_STYLES } from '@/pages/shared/uploads/pages/components/InSituDataCards';
import { DateLike, printDDMMYYYY } from '@/utils/formatting/date';

import { MediaMapDraftItem } from '../utils/mediaMapItem';

const MediaMapItemDialog = lazy(() => import('./MediaMapItemDialog'));

type Props = {
  item: MediaMapDraftItem;
  configurationId: string;
  onDelete: (id: string) => void;
  onUpdate: (id: string, item: Omit<MediaMapDraftItem, 'id'>) => void;
  showActionButtons: boolean;
};

export const MediaMapItemCard = ({ item, configurationId, onDelete, onUpdate, showActionButtons }: Props) => {
  const { t } = useTranslation();
  const [showMore, setShowMore] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);

  const formattedDate = printDDMMYYYY(item.reference_date as DateLike);

  const hasHiddenData =
    item.label_description ||
    item.label_mode_of_sight ||
    item.label_time_of_sight ||
    item.label_credits ||
    item.label_url;

  return (
    <>
      <ActionCard
        className='w-[320px] h-auto'
        actionButtons={
          showActionButtons && (
            <Stack direction='row' className='pt-1 justify-between w-full' spacing={2}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={() => setShowDeleteDialog(true)}
                    data-testid='media-map-item-delete-button'
                    className={`text-error hover:text-error-dark ${BUTTON_STYLES}`}
                    variant='text'
                  >
                    <i className='ri-delete-bin-line text-xl' />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className='whitespace-pre-wrap'>
                  <Stack className='gap-4'>{t('global.ui.buttons.delete')}</Stack>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={() => setShowEditDialog(true)}
                    data-testid='media-map-item-edit-button'
                    className={BUTTON_STYLES}
                  >
                    <i className='ri-pencil-fill text-xl' />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className='whitespace-pre-wrap'>
                  <Stack className='gap-4'>{t('global.ui.buttons.edit')}</Stack>
                </TooltipContent>
              </Tooltip>
            </Stack>
          )
        }
      >
        <Stack spacing={4} className='h-full'>
          <Stack center centerMain className='grow w-full'>
            <MediaFilePreview
              fileInfoId={item.value}
              className='w-full h-40 object-cover'
              audio={{
                width: 300,
                height: 40,
              }}
            />
          </Stack>
          <Stack spacing={2} className='px-2'>
            {item.label_species && (
              <LabelRow label={t('shared.mediaMaps.configuration.card.speciesName')} className='italic'>
                {item.label_species}
              </LabelRow>
            )}
            {item.label_common_name && (
              <LabelRow label={t('shared.mediaMaps.configuration.card.commonName')}>{item.label_common_name}</LabelRow>
            )}
            <LabelRow label={t('shared.mediaMaps.configuration.card.plot')}>{item.plot_name}</LabelRow>
            {formattedDate && (
              <LabelRow label={t('shared.mediaMaps.configuration.card.date')}>{formattedDate}</LabelRow>
            )}
            {showMore && (
              <>
                {item.label_description && (
                  <LabelRow label={t('shared.mediaMaps.configuration.card.description')}>
                    {item.label_description}
                  </LabelRow>
                )}
                {item.label_mode_of_sight && (
                  <LabelRow label={t('shared.mediaMaps.configuration.card.modeOfSight')}>
                    {item.label_mode_of_sight}
                  </LabelRow>
                )}
                {item.label_time_of_sight && (
                  <LabelRow label={t('shared.mediaMaps.configuration.card.timeOfSight')}>
                    {item.label_time_of_sight}
                  </LabelRow>
                )}
                {item.label_credits && (
                  <LabelRow label={t('shared.mediaMaps.configuration.card.credits')}>{item.label_credits}</LabelRow>
                )}
                {item.label_url && (
                  <LabelRow label={t('shared.mediaMaps.configuration.card.url')}>
                    <Url url={item.label_url} />
                  </LabelRow>
                )}
              </>
            )}
          </Stack>
          {hasHiddenData && (
            <Button
              variant='text'
              className='self-start p-0 typography-body1 underline'
              onClick={() => setShowMore((v) => !v)}
            >
              {showMore ? t('global.analysis.seeLess') : t('global.analysis.seeMore')}
            </Button>
          )}
        </Stack>
      </ActionCard>
      <ConfirmationDialog
        open={showDeleteDialog}
        header={t('shared.mediaMaps.configuration.deleteDialog.header')}
        body={t('shared.mediaMaps.configuration.deleteDialog.body')}
        CancelAction={
          <Button variant='outline' onClick={() => setShowDeleteDialog(false)}>
            {t('global.ui.buttons.cancel')}
          </Button>
        }
        PrimaryAction={
          <Button
            className='bg-error hover:bg-error-dark'
            onClick={() => {
              setShowDeleteDialog(false);
              onDelete(item.id);
            }}
          >
            {t('global.ui.buttons.delete')}
          </Button>
        }
      />
      {showEditDialog && (
        <MediaMapItemDialog
          open={showEditDialog}
          onClose={() => setShowEditDialog(false)}
          configurationId={configurationId}
          editItem={item}
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          onAdd={() => {}}
          onUpdate={onUpdate}
        />
      )}
    </>
  );
};

const LabelRow = ({ label, children, className }: { label: string; children: ReactNode; className?: string }) => (
  <Stack direction='row' spacing={2} className='flex-wrap'>
    <span className='typography-body1 text-text-secondary'>{label}</span>
    <span className={`typography-body1 ${className}`}>{children}</span>
  </Stack>
);
